import { useEffect, useRef, useCallback } from 'react';
import { gaTimeTracker } from '@app/components/common/analytics/analyticsUtils';

/**
 * Custom hook that uses MutationObserver to track the time between loading a node and the time when it is added
 * to the DOM and displayed in the UI. Additionally it receives some matchers to observe an specific section of the DOM
 *
 * @param {string} eventID - Unique identifier for each node.
 * @param {string} loaderSelector - Class selector for nodes that hold loaders.
 * @param {string} afterMutationSelector - Attribute selector for nodes looking for an specific attribute in the resultant element after loading.
 * @param {string} trackedServiceName - Service name asociated with the tracked data.
 *
 * @returns {React.MutableRefObject} - Reference to the DOM node used as a fallback reference fo the hook.
 */
const useMutationObserverLoaderTime = (
  eventID,
  loaderSelector,
  afterMutationSelector,
  trackedServiceName
) => {
  /**
   * Reference to the DOM node used as a fallback for the hook
   * @type {React.MutableRefObject}
   */
  const observableRef = useRef(null);

  /**
   * Reference to track the start time when a loader node is detected
   * @type {React.MutableRefObject}
   */
  const startTimingRef = useRef(null);

  const finishTimeTracking = useCallback(
    node => {
      if (node?.matches(afterMutationSelector)) {
        let timing = startTimingRef.current
          ? Math.floor(new Date() - startTimingRef.current)
          : 0;
        gaTimeTracker(timing, trackedServiceName, eventID);
        startTimingRef.current = null;
      }
    },
    [afterMutationSelector, eventID, trackedServiceName]
  );

  useEffect(() => {
    /**
     * Callback function for the MutationObserver
     * @param {MutationRecord[]} mutations - List of observed mutations.
     */
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        for (const node of mutation.addedNodes) {
          if (node?.matches(loaderSelector)) {
            startTimingRef.current = new Date();
          } else {
            finishTimeTracking(node);
          }
        }
      });
    });

    if (observableRef?.current) {
      observer.observe(observableRef?.current, {
        childList: true,
        subtree: true
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [
    eventID,
    loaderSelector,
    afterMutationSelector,
    trackedServiceName,
    finishTimeTracking
  ]);

  return observableRef;
};

export default useMutationObserverLoaderTime;
